import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {DownloadCallbackResponse} from '@Libraries/poster-download-library';
import type {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';

export enum DownloadStateTypes {
  DOWNLOADING = 'downloading',
  READY = 'ready',
  ERROR = 'error',
}

export interface DownloadOrderDownloadingState {
  orderId: string;
  orderIdKey: string;
  state: DownloadStateTypes.DOWNLOADING;
  downloadResult?: DownloadCallbackResponse;
  downloadProgress?: number;
  displayProgress?: number;
  name: string;
  fileType: FileTypeOptions;
  isGoogleDriveDownload?: boolean;
  isDropboxDownload?: boolean;
  thumbnailUrl?: string;
  isDownloadPageOrder?: boolean;
  isFreebieDownloadPageOrder?: boolean;
  isPaygDownload?: boolean;
  generationKey?: string;
  isMultipageDownload?: boolean;
  downloadAsIndividualFiles?: boolean;
  selectedPages?: string[];
  isHeavyOrder?: boolean;
}

export interface DownloadOrderReadyState {
  orderId: string;
  orderIdKey: string;
  state: DownloadStateTypes.READY;
  downloadURL: string;
  downloadProgress?: number;
  displayProgress?: number;
  downloadResult?: DownloadCallbackResponse;
  name: string;
  fileType: FileTypeOptions;
  isGoogleDriveDownload?: boolean;
  isDropboxDownload?: boolean;
  thumbnailUrl?: string;
  isDownloadPageOrder?: boolean;
  isFreebieDownloadPageOrder?: boolean;
  isPaygDownload?: boolean;
  generationKey?: string;
  isMultipageDownload?: boolean;
  downloadAsIndividualFiles?: boolean;
  selectedPages?: string[];
  isHeavyOrder?: boolean;
}

export interface DownloadOrderErrorState {
  orderId: string;
  orderIdKey: string;
  state: DownloadStateTypes.ERROR;
  downloadProgress?: number;
  displayProgress?: number;
  downloadResult?: DownloadCallbackResponse;
  name: string;
  fileType: FileTypeOptions;
  isGoogleDriveDownload?: boolean;
  isDropboxDownload?: boolean;
  thumbnailUrl?: string;
  isDownloadPageOrder?: boolean;
  isFreebieDownloadPageOrder?: boolean;
  isPaygDownload?: boolean;
  generationKey?: string;
  isMultipageDownload?: boolean;
  downloadAsIndividualFiles?: boolean;
  selectedPages?: string[];
  isHeavyOrder?: boolean;
}

export type OrderDownloadStateNew = DownloadOrderDownloadingState | DownloadOrderReadyState | DownloadOrderErrorState;

interface State {
  orderStateHashmapNew: Record<string, OrderDownloadStateNew>;
  currentlyProcessingOrderIdKeys: string[];
  dropboxProcessingOrderIdKeys: string[];
  pollingTrigger: number;
  downloadPagePollingTrigger: number;
  paygPollingTrigger: number;
  currentViewingOrderIdKey?: string;
  isViewingDropboxDownloads: boolean;
  webDownloadPosterTypeNames?: string[];
  upsellingRecommendationNumber: number | null;
  showLoadingPopup?: boolean;
  isInnerDropdownOpen: boolean;
  isSliderBeingDragged?: boolean;
  panelOpenState?: boolean;
  isExternalModalOpen?: boolean;
}

const initialState: State = {
  orderStateHashmapNew: {},
  currentlyProcessingOrderIdKeys: [],
  dropboxProcessingOrderIdKeys: [],
  pollingTrigger: 0,
  downloadPagePollingTrigger: 0,
  paygPollingTrigger: 0,
  currentViewingOrderIdKey: undefined,
  isViewingDropboxDownloads: false,
  webDownloadPosterTypeNames: [],
  upsellingRecommendationNumber: null,
  showLoadingPopup: true,
  isInnerDropdownOpen: false,
  isSliderBeingDragged: false,
  panelOpenState: false,
  isExternalModalOpen: false,
};

export const downloadPanelSlice = createSlice({
  name: 'downloadDialog',
  initialState,
  reducers: {
    updateWebDownloadPosterTypeNames: (state, action: PayloadAction<string[]>) => {
      state.webDownloadPosterTypeNames = action.payload;
    },
    setUpsellingRecommendationNumberIfNeeded: (state, action: PayloadAction<number | null>) => {
      if (action.payload === null) {
        state.upsellingRecommendationNumber = null;
        return;
      }
      state.upsellingRecommendationNumber ??= action.payload;
    },
    updateDownloadLoadingPopupOpenState: (state, action: PayloadAction<boolean>) => {
      state.showLoadingPopup = action.payload;
    },
    toggleInnerDropdownOpenState: (state, action: PayloadAction<boolean>) => {
      state.isInnerDropdownOpen = action.payload;
    },
    updateSliderDragState: (state, action: PayloadAction<boolean>) => {
      state.isSliderBeingDragged = action.payload;
    },
    setCurrentViewingOrderIdKey: (state, action: PayloadAction<string | undefined>) => {
      state.currentViewingOrderIdKey = action.payload;
    },
    setIsViewingDropboxDownloads: (state, action: PayloadAction<boolean>) => {
      state.isViewingDropboxDownloads = action.payload;
    },
    removeOrder: (state, action: PayloadAction<string>) => {
      const orderIdKey = action.payload;
      delete state.orderStateHashmapNew[orderIdKey];
      state.currentlyProcessingOrderIdKeys = state.currentlyProcessingOrderIdKeys.filter((id) => id !== orderIdKey);
      if (state.currentViewingOrderIdKey === orderIdKey) {
        state.currentViewingOrderIdKey = undefined;
      }
    },
    addToCurrentProcessingIds: (state, action: PayloadAction<string>) => {
      if (!state.currentlyProcessingOrderIdKeys.includes(action.payload)) {
        state.currentlyProcessingOrderIdKeys = [...(state.currentlyProcessingOrderIdKeys ?? []), action.payload];
      }
    },
    removeFromCurrentProcessingIds: (state, action: PayloadAction<string>) => {
      state.currentlyProcessingOrderIdKeys = state.currentlyProcessingOrderIdKeys?.filter((id) => id !== action.payload);
    },
    addToDropboxProcessingIds: (state, action: PayloadAction<string>) => {
      if (!state.dropboxProcessingOrderIdKeys.includes(action.payload)) {
        state.dropboxProcessingOrderIdKeys = [...(state.dropboxProcessingOrderIdKeys ?? []), action.payload];
      }
    },
    removeFromDropboxProcessingIds: (state, action: PayloadAction<string>) => {
      state.dropboxProcessingOrderIdKeys = state.dropboxProcessingOrderIdKeys?.filter((id) => id !== action.payload);
    },
    clearDropboxProcessingIds: (state) => {
      state.dropboxProcessingOrderIdKeys = [];
    },
    updateOrderStateNew: (state, action: PayloadAction<OrderDownloadStateNew>) => {
      if (state.orderStateHashmapNew[action.payload.orderIdKey]) {
        state.orderStateHashmapNew[action.payload.orderIdKey] = {
          ...state.orderStateHashmapNew[action.payload.orderIdKey],
          ...action.payload,
        };
      }
    },
    updateDisplayProgressNew: (state, action: PayloadAction<{orderIdKey: string; displayProgress: number}>) => {
      const {orderIdKey, displayProgress} = action.payload;
      if (state.orderStateHashmapNew[orderIdKey]) {
        state.orderStateHashmapNew[orderIdKey].displayProgress = displayProgress;
      }
    },
    addOrderStateNew: (state, action: PayloadAction<OrderDownloadStateNew>) => {
      state.orderStateHashmapNew[action.payload.orderIdKey] = {
        displayProgress: 0,
        ...action.payload,
      };
      if (action.payload.state === DownloadStateTypes.DOWNLOADING) {
        if (action.payload.isDownloadPageOrder || action.payload.isFreebieDownloadPageOrder) {
          state.downloadPagePollingTrigger += 1;
        } else if (action.payload.isPaygDownload) {
          state.paygPollingTrigger += 1;
        } else {
          state.pollingTrigger += 1;
        }
      }
    },
    updateDownloadPanelOpenState: (state, action: PayloadAction<boolean>) => {
      state.panelOpenState = action.payload;
    },
    updateExternalModalOpenStateForDownloadPanel: (state, action: PayloadAction<boolean>) => {
      state.isExternalModalOpen = action.payload;
    },
  },
});

export const {
  updateDisplayProgressNew,
  setUpsellingRecommendationNumberIfNeeded,
  addToCurrentProcessingIds,
  removeFromCurrentProcessingIds,
  addToDropboxProcessingIds,
  removeFromDropboxProcessingIds,
  clearDropboxProcessingIds,
  updateExternalModalOpenStateForDownloadPanel,
  updateOrderStateNew,
  addOrderStateNew,
  updateWebDownloadPosterTypeNames,
  updateDownloadLoadingPopupOpenState,
  toggleInnerDropdownOpenState,
  updateSliderDragState,
  setCurrentViewingOrderIdKey,
  setIsViewingDropboxDownloads,
  removeOrder,
  updateDownloadPanelOpenState,
} = downloadPanelSlice.actions;
export const downloadReducer = downloadPanelSlice.reducer;
